import React, { useEffect, useState } from 'react'
import Select, { MultiValue, SingleValue } from 'react-select'
import styled from 'styled-components'
import { Input } from '@workwhile/ui'
import { defs } from 'components/Shared/defs'
import { device } from 'components/Shared/device'
import {
  JET_CALL_REPORT_STATUS_OPTIONS,
  JetCallReportStatus,
} from 'pages/Jet/shared/constants/status'
import { useJetStatusFiltersQueryState } from '../hooks/useJetStatusFiltersQueryState'

type JetStatusFilters = ReturnType<typeof useJetStatusFiltersQueryState>[0]

type StatusOption = { label: string; value: JetCallReportStatus }
type DaysAgoOption = { label: string; value: number }

// 0 is a sentinel for "no limit" - see listJetCallReports.ts.
const DAYS_AGO_OPTIONS: DaysAgoOption[] = [
  { label: 'Last 7 days', value: 7 },
  { label: 'Last 14 days', value: 14 },
  { label: 'Last 30 days', value: 30 },
  { label: 'Last 90 days', value: 90 },
  { label: 'Forever', value: 0 },
]

const SEARCH_DEBOUNCE_MS = 300

const findSelectedStatusOptions = (values: JetCallReportStatus[] | null) =>
  values
    ? JET_CALL_REPORT_STATUS_OPTIONS.filter((o) => values.includes(o.value))
    : []

const toStatusValues = (selected: MultiValue<StatusOption>) =>
  selected.length ? selected.map((o) => o.value) : null

type JetStatusFilterBarProps = {
  filters: JetStatusFilters
  setFilters: (update: Partial<JetStatusFilters>) => void
}

export const JetStatusFilterBar = ({
  filters,
  setFilters,
}: JetStatusFilterBarProps) => {
  const [searchInput, setSearchInput] = useState(filters.search)

  // Keep in sync with URL-driven changes (e.g. back/forward navigation).
  useEffect(() => {
    setSearchInput(filters.search)
  }, [filters.search])

  useEffect(() => {
    if (searchInput === filters.search) {
      return
    }
    const timeout = setTimeout(() => {
      setFilters({ search: searchInput })
    }, SEARCH_DEBOUNCE_MS)
    return () => clearTimeout(timeout)
  }, [searchInput, filters.search, setFilters])

  const selectedDaysAgo =
    DAYS_AGO_OPTIONS.find((o) => o.value === filters.daysAgo) ?? null

  return (
    <FilterBarWrapper showBorderTop showBorderBottom>
      <Input
        type="text"
        placeholder="Search assignment, association, shift, or worker ID"
        value={searchInput}
        onChange={(e) => setSearchInput(e.target.value)}
        style={{ minWidth: '350px' }}
      />
      <Select
        isMulti
        isClearable
        isSearchable={false}
        options={JET_CALL_REPORT_STATUS_OPTIONS}
        styles={customStyles}
        name="statuses"
        placeholder="Status"
        value={findSelectedStatusOptions(filters.statuses)}
        onChange={(selected: MultiValue<StatusOption>) =>
          setFilters({ statuses: toStatusValues(selected) })
        }
        closeMenuOnSelect={false}
      />
      <Select
        isSearchable={false}
        options={DAYS_AGO_OPTIONS}
        styles={customStyles}
        name="daysAgo"
        value={selectedDaysAgo}
        onChange={(selected: SingleValue<DaysAgoOption>) => {
          if (selected) {
            setFilters({ daysAgo: selected.value })
          }
        }}
      />
    </FilterBarWrapper>
  )
}

const customStyles = {
  container: (provided: Record<string, unknown>) => ({
    ...provided,
    minWidth: 220,
    fontSize: defs.fontS,
  }),
}

const FilterBarWrapper = styled.div<{
  showBorderTop?: boolean
  showBorderBottom?: boolean
}>`
  border-bottom: ${(props) => (props.showBorderBottom ? '1px solid' : null)};
  border-top: ${(props) => (props.showBorderTop ? '1px solid' : null)};
  border-color: ${defs.veryLightBlack};
  padding: ${defs.paddingS};
  display: flex;
  gap: 1rem;
  flex-direction: row;
  flex-wrap: wrap;
  align-items: center;
  background-color: #fafafa;
  @media ${device.smallerThanMobileL} {
    font-size: ${defs.fontS};
    height: 100%;
    padding-left: ${defs.paddingM};
  }
`
